import React, { useState } from 'react';
import {
  IconButton,
  MenuItem,
  styled,
  Tooltip,
  tooltipClasses,
  TooltipProps,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { EmbeddableMenu } from '@/integrations/EmbeddableMenu';
import { AccountBase } from '@/models/AccountViewModel';
import { useLocalization } from '@/hooks/useLocalization';

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 225,
  },
});

interface AccountActionsMenuProps {
  account: AccountBase;
  /** Called when Edit Account is chosen. Receives the account so the parent can open the edit modal without opening the row-detail flyout. */
  handleEdit: (account: AccountBase) => void;
  /** Called with the row account so the parent can set selectedAccount before opening the dialog. */
  handleMarkAsClosed: (account: AccountBase) => void;
  /** Called with the row account so the parent can set selectedAccount before opening the dialog. */
  handleUploadHistory: (account: AccountBase) => void;
  /** Called with the row account so the parent can set selectedAccount before opening the dialog. */
  handleDelete: (account: AccountBase) => void;
}

const AccountActionsMenu: React.FC<AccountActionsMenuProps> = ({
  account,
  handleEdit,
  handleMarkAsClosed,
  handleUploadHistory,
  handleDelete,
}) => {
  const { t } = useLocalization();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showEditModal = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    handleEdit(account);
    handleClose();
  };

  const showMarkAsClosedDialog = () => {
    handleMarkAsClosed(account);
    handleClose();
  };

  const showUploadHistoryDialog = () => {
    handleUploadHistory(account);
    handleClose();
  };

  const showDeleteDialog = () => {
    handleDelete(account);
    handleClose();
  };

  /**
   * End-users should not be able to perform any of these actions (edit / closed / history / delete)
   * on feed-driven accounts.
   */
  if (!account.CanEdit) {
    return (
      <CustomWidthTooltip
        title={t('accountEditDisabled')}
        placement='bottom-end'
        arrow
        slotProps={{
          popper: {
            sx: {
              [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                {
                  marginTop: '0px',
                },
              [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                {
                  marginBottom: '0px',
                },
              [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                {
                  marginLeft: '0px',
                },
              [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                {
                  marginRight: '0px',
                },
            },
          },
        }}
      >
        <span>
          <IconButton
            aria-label='more'
            disabled
          >
            <MoreVertIcon />
          </IconButton>
        </span>
      </CustomWidthTooltip>
    );
  }

  return (
    <>
      <IconButton
        aria-label='more'
        aria-controls='long-menu'
        aria-haspopup='true'
        onClick={handleClick}
      >
        <MoreVertIcon />
      </IconButton>
      <EmbeddableMenu
        id='long-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={showEditModal}>Edit Account</MenuItem>
        <MenuItem onClick={showMarkAsClosedDialog}>Mark as Closed</MenuItem>
        <MenuItem onClick={showUploadHistoryDialog}>
          Upload Account History
        </MenuItem>
        <MenuItem onClick={showDeleteDialog}>Delete</MenuItem>
      </EmbeddableMenu>
    </>
  );
};

export default AccountActionsMenu;
